import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTenant } from './TenantContext';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/storage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { tenantName } = useTenant();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = safeGetItem(`minibank_token_${tenantName}`);
    const storedUser = safeGetItem(`minibank_user_${tenantName}`);
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.warn('Failed to parse stored user data:', e);
        // Если не удалось распарсить данные пользователя, удаляем их
        safeRemoveItem(`minibank_token_${tenantName}`);
        safeRemoveItem(`minibank_user_${tenantName}`);
      }
    }
    
    setLoading(false);
  }, [tenantName]);

  const login = async (email, password) => {
    try {
      // Use correct API URL for production (nginx proxy) or development (direct port)
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
      
      const apiUrl = isDevelopment 
        ? `${protocol}//${hostname}:3001/${tenantName}/auth/login`
        : `${protocol}//${hostname}/api/${tenantName}/auth/login`;
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      const data = await response.json();
      
      setToken(data.token);
      setUser(data.user);
      
      // Store in localStorage
      safeSetItem(`minibank_token_${tenantName}`, data.token);
      safeSetItem(`minibank_user_${tenantName}`, JSON.stringify(data.user));
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        // Use correct API URL for production (nginx proxy) or development (direct port)
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
        
        const apiUrl = isDevelopment 
          ? `${protocol}//${hostname}:3001/${tenantName}/auth/logout`
          : `${protocol}//${hostname}/api/${tenantName}/auth/logout`;
          
        await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      safeRemoveItem(`minibank_token_${tenantName}`);
      safeRemoveItem(`minibank_user_${tenantName}`);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 